/**
 * @fileoverview Página responsável por listar e administrar comunicados da plataforma.
 */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import { Pencil, Trash2 as Trash, Search, Paperclip } from "lucide-react";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(4),
    paddingTop: theme.spacing(3),
    overflowY: "scroll",
    backgroundColor: "transparent",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.5)' : '1px solid #3D5CFD',
        boxShadow: theme.palette.type === 'dark'
          ? '0 0 0 1px rgba(91, 124, 255, 0.15)'
          : '0 0 0 1px rgba(61, 92, 253, 0.1)',
      },
      '& fieldset': {
        border: 'none',
      },
    },
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  card: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.6) 0%, rgba(22, 22, 24, 0.6) 100%)'
      : '#FFFFFF',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: "20px",
    boxShadow: theme.palette.type === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
      : '0 4px 20px rgba(0, 0, 0, 0.08)',
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: 'visible',
    position: 'relative',
    "&:hover": {
      transform: 'translateY(-4px)',
      boxShadow: theme.palette.type === 'dark'
        ? '0 12px 40px rgba(91, 124, 255, 0.25)'
        : '0 8px 30px rgba(61, 92, 253, 0.15)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(61, 92, 253, 0.2)',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  announcementTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, #FFFFFF 0%, rgba(255, 255, 255, 0.8) 100%)'
      : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: theme.spacing(1),
  },
  announcementId: {
    fontSize: '0.875rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
    fontWeight: 500,
  },
  announcementInfo: {
    fontSize: '0.9rem',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#475569',
    marginTop: theme.spacing(1),
    '& strong': {
      color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
    },
  },
  cardActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  iconButton: {
    padding: theme.spacing(1),
    borderRadius: '10px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  editButton: {
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(59, 130, 246, 0.15)'
        : 'rgba(59, 130, 246, 0.08)',
    },
  },
  deleteButton: {
    color: theme.palette.type === 'dark' ? '#fca5a5' : '#ef4444',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(239, 68, 68, 0.08)',
    },
  },
  priorityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  highPriority: {
    background: theme.palette.type === 'dark'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(239, 68, 68, 0.1)',
    color: theme.palette.type === 'dark' ? '#fca5a5' : '#dc2626',
    border: theme.palette.type === 'dark' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(239, 68, 68, 0.2)',
  },
  mediumPriority: {
    background: theme.palette.type === 'dark'
      ? 'rgba(251, 191, 36, 0.15)'
      : 'rgba(251, 191, 36, 0.1)',
    color: theme.palette.type === 'dark' ? '#fcd34d' : '#d97706',
    border: theme.palette.type === 'dark' ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(251, 191, 36, 0.2)',
  },
  lowPriority: {
    background: theme.palette.type === 'dark'
      ? 'rgba(34, 197, 94, 0.15)'
      : 'rgba(34, 197, 94, 0.1)',
    color: theme.palette.type === 'dark' ? '#86efac' : '#16a34a',
    border: theme.palette.type === 'dark' ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(34, 197, 94, 0.2)',
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  activeStatus: {
    background: theme.palette.type === 'dark'
      ? 'rgba(34, 197, 94, 0.15)'
      : 'rgba(34, 197, 94, 0.1)',
    color: theme.palette.type === 'dark' ? '#86efac' : '#16a34a',
    border: theme.palette.type === 'dark' ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(34, 197, 94, 0.2)',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  inactiveStatus: {
    background: theme.palette.type === 'dark'
      ? 'rgba(156, 163, 175, 0.15)'
      : 'rgba(156, 163, 175, 0.1)',
    color: theme.palette.type === 'dark' ? '#d1d5db' : '#6b7280',
    border: theme.palette.type === 'dark' ? '1px solid rgba(156, 163, 175, 0.3)' : '1px solid rgba(156, 163, 175, 0.2)',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
}));

const Announcements = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);

  const socketManager = useContext(SocketContext);

  // trava para nao acessar pagina que não pode  
  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toast.error(i18n.t("announcements.toasts.info"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-announcement`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [socketManager, user.companyId]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName)
      await api.delete(`/announcements/${announcement.id}/media-upload`);

      await api.delete(`/announcements/${announcement.id}`);
      
      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingAnnouncement(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const translatePriority = (val) => {
    if (val === 1) {
      return `🔴 ${i18n.t("announcements.high")}`;
    }
    if (val === 2) {
      return `🟡 ${i18n.t("announcements.medium")}`;
    }
    if (val === 3) {
      return `🟢 ${i18n.t("announcements.low")}`;
    }
  };

  const getPriorityClass = (val) => {
    if (val === 1) return classes.highPriority;
    if (val === 2) return classes.mediumPriority;
    if (val === 3) return classes.lowPriority;
    return classes.lowPriority;
  };

  return (
    <MainContainer >
      <ConfirmationModal
        title={
          deletingAnnouncement &&
          `${i18n.t("announcements.confirmationModal.deleteTitle")} ${deletingAnnouncement.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <AnnouncementModal
        resetPagination={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />
      <MainHeader>
        <Title>{i18n.t("announcements.title")} ({announcements.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("announcements.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchField}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAnnouncementModal}
          >
            {i18n.t("announcements.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        onScroll={handleScroll}
      >
        <div className={classes.cardsContainer}>
          {announcements.sort((a, b) => a.id - b.id).map((announcement) => (
            <Card key={announcement.id} className={classes.card}>
              <CardContent>
                <div className={classes.cardHeader}>
                  <div style={{ flex: 1 }}>
                    <Typography className={classes.announcementTitle}>
                      {announcement.title}
                    </Typography>
                    <Typography className={classes.announcementId}>
                      ID: {announcement.id}
                    </Typography>
                  </div>
                  <div className={classes.cardActions}>
                    <IconButton
                      size="small"
                      className={`${classes.iconButton} ${classes.editButton}`}
                      onClick={() => handleEditAnnouncement(announcement)}
                      title="Editar"
                    >
                      <Pencil size={18} strokeWidth={1.5} color="#5490ff" />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={`${classes.iconButton} ${classes.deleteButton}`}
                      onClick={(e) => {
                        setConfirmModalOpen(true);
                        setDeletingAnnouncement(announcement);
                      }}
                      title="Deletar"
                    >
                      <Trash size={18} strokeWidth={1.5} color="#c72b40" />
                    </IconButton>
                  </div>
                </div>

                <Box display="flex" alignItems="center" flexWrap="wrap" mt={1}>
                  <span className={`${classes.priorityBadge} ${getPriorityClass(announcement.priority)}`}>
                    {translatePriority(announcement.priority)}
                  </span>
                  <Chip
                    label={announcement.status ? i18n.t("announcements.active") : i18n.t("announcements.inactive")}
                    className={`${classes.statusChip} ${announcement.status ? classes.activeStatus : classes.inactiveStatus}`}
                    size="small"
                  />
                </Box>

                {announcement.mediaName && (
                  <Typography className={classes.announcementInfo} style={{ marginTop: 12 }}>
                    <Paperclip style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 4 }} />
                    <strong>{announcement.mediaName}</strong>
                  </Typography>
                )}

                {announcement.text && (
                  <Typography className={classes.announcementInfo} style={{ marginTop: 12 }}>
                    {announcement.text.length > 100
                      ? `${announcement.text.substring(0, 100)}...`
                      : announcement.text}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </Paper>
    </MainContainer >
  )
};

export default Announcements;
